import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { VOCABULARIES } from 'volto-ministerio-publico-federal/config/vocabulary-config';

const TITLE_RECOMMENDED_LENGTH = 60;

export const DEFAULT_LIMIT = 3;

export type LinkMode = 'auto' | 'custom';

export interface NewsByThemeData {
  '@type'?: string;
  title?: string;
  tema?: string;
  unidade?: string;
  limit?: number;
  linkMode?: LinkMode;
  buttonLabel?: string;
  href?: { '@id': string }[];
  external?: boolean;
}

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_noticias_por_tema_block',
    defaultMessage: 'Notícias por tema',
  },
  fieldsetContent: {
    id: 'mpf_noticias_por_tema_fieldset_content',
    defaultMessage: 'Conteúdo',
  },
  fieldsetButton: {
    id: 'mpf_noticias_por_tema_fieldset_button',
    defaultMessage: 'Botão',
  },
  title: {
    id: 'mpf_noticias_por_tema_title',
    defaultMessage: 'Título da seção',
  },
  titleHelp: {
    id: 'mpf_noticias_por_tema_title_help',
    defaultMessage:
      'Cabeçalho exibido acima dos cards. Deixe em branco para não mostrar cabeçalho nenhum.',
  },
  titlePlaceholder: {
    id: 'mpf_noticias_por_tema_title_placeholder',
    defaultMessage: 'Ex.: Notícias sobre meio ambiente',
  },
  tema: {
    id: 'mpf_noticias_por_tema_tema',
    defaultMessage: 'Tema',
  },
  temaHelp: {
    id: 'mpf_noticias_por_tema_tema_help',
    defaultMessage:
      'Categoria das notícias que o bloco vai listar. São exibidas sempre as mais recentes já publicadas nesse tema — a lista se atualiza sozinha conforme novas notícias saem.',
  },
  unidade: {
    id: 'mpf_noticias_por_tema_unidade',
    defaultMessage: 'Unidade de origem',
  },
  unidadeHelp: {
    id: 'mpf_noticias_por_tema_unidade_help',
    defaultMessage:
      'Opcional. Restringe as notícias a uma unidade específica — use apenas quando a página for de uma unidade, porque um mesmo tema costuma ter notícias produzidas por várias áreas. Em branco, traz notícias de todas as unidades.',
  },
  limit: {
    id: 'mpf_noticias_por_tema_limit',
    defaultMessage: 'Quantidade de notícias',
  },
  limitHelp: {
    id: 'mpf_noticias_por_tema_limit_help',
    defaultMessage: 'Quantos cards exibir. O padrão do desenho são 3.',
  },
  linkMode: {
    id: 'mpf_noticias_por_tema_link_mode',
    defaultMessage: 'Destino do botão',
  },
  linkModeHelp: {
    id: 'mpf_noticias_por_tema_link_mode_help',
    defaultMessage:
      'No automático, o botão leva para a lista completa de notícias já filtrada pelo tema (e pela unidade, se houver). Escolha "Link personalizado" para apontar para outro lugar.',
  },
  linkModeAuto: {
    id: 'mpf_noticias_por_tema_link_mode_auto',
    defaultMessage: 'Automático (filtro pelo tema)',
  },
  linkModeCustom: {
    id: 'mpf_noticias_por_tema_link_mode_custom',
    defaultMessage: 'Link personalizado',
  },
  href: {
    id: 'mpf_noticias_por_tema_href',
    defaultMessage: 'Link',
  },
  hrefHelp: {
    id: 'mpf_noticias_por_tema_href_help',
    defaultMessage:
      'Destino do botão — uma página do próprio Portal MPF ou um site externo. Se ficar vazio, o botão volta a usar o filtro automático.',
  },
  external: {
    id: 'mpf_noticias_por_tema_external',
    defaultMessage: 'Abrir em nova aba',
  },
  externalHelp: {
    id: 'mpf_noticias_por_tema_external_help',
    defaultMessage:
      'Controla só se o link abre numa aba nova — não interfere no destino escolhido acima.',
  },
  buttonLabel: {
    id: 'mpf_noticias_por_tema_button_label',
    defaultMessage: 'Texto do botão',
  },
  buttonLabelHelp: {
    id: 'mpf_noticias_por_tema_button_label_help',
    defaultMessage:
      'Prefira um texto que diga para onde o botão leva — evite "clique aqui" ou "saiba mais".',
  },
});

export const mpfNewsByThemeSchema = ({
  intl,
  data = {},
}: {
  intl: IntlShape;
  data?: NewsByThemeData;
}) => {
  const isCustomLink = data.linkMode === 'custom';

  return {
    title: intl.formatMessage(messages.titleBlock),
    fieldsets: [
      {
        id: 'default',
        title: intl.formatMessage(messages.fieldsetContent),
        fields: ['title', 'tema', 'unidade', 'limit'],
      },
      {
        id: 'button',
        title: intl.formatMessage(messages.fieldsetButton),
        fields: isCustomLink
          ? ['linkMode', 'href', 'external', 'buttonLabel']
          : ['linkMode', 'buttonLabel'],
      },
    ],
    properties: {
      title: {
        title: intl.formatMessage(messages.title),
        description: intl.formatMessage(messages.titleHelp),
        placeholder: intl.formatMessage(messages.titlePlaceholder),
        widget: 'charCount',
        recommendedLength: TITLE_RECOMMENDED_LENGTH,
      },
      tema: {
        title: intl.formatMessage(messages.tema),
        description: intl.formatMessage(messages.temaHelp),
        vocabulary: { '@id': VOCABULARIES.NEWS_THEMES.name },
      },
      unidade: {
        title: intl.formatMessage(messages.unidade),
        description: intl.formatMessage(messages.unidadeHelp),
        vocabulary: { '@id': VOCABULARIES.ORIGIN_UNITS.name },
      },
      limit: {
        title: intl.formatMessage(messages.limit),
        description: intl.formatMessage(messages.limitHelp),
        type: 'number',
        minimum: 1,
        maximum: 12,
        default: DEFAULT_LIMIT,
      },
      linkMode: {
        title: intl.formatMessage(messages.linkMode),
        description: intl.formatMessage(messages.linkModeHelp),
        choices: [
          ['auto', intl.formatMessage(messages.linkModeAuto)],
          ['custom', intl.formatMessage(messages.linkModeCustom)],
        ],
        default: 'auto',
        noValueOption: false,
      },
      href: {
        title: intl.formatMessage(messages.href),
        description: intl.formatMessage(messages.hrefHelp),
        widget: 'object_browser',
        mode: 'link',
        selectedItemAttrs: ['@id', '@type', 'Title'],
        allowExternals: true,
        initialPath: '/',
      },
      external: {
        title: intl.formatMessage(messages.external),
        description: intl.formatMessage(messages.externalHelp),
        type: 'boolean',
      },
      buttonLabel: {
        title: intl.formatMessage(messages.buttonLabel),
        description: intl.formatMessage(messages.buttonLabelHelp),
      },
    },
    required: ['tema'],
  };
};

export default mpfNewsByThemeSchema;
